import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import OnSpotAccessGate from '../../components/auth/OnSpotAccessGate';
import EventRegistration from '../EventRegistration';
import { useEventRoute } from '../../utils/eventRef';
import { useTicketing } from '../../utils/ticketing';
import TicketingLanding from './TicketingLanding';
import PassPicker from './PassPicker';
import BookingForm from './BookingForm';

// Links into the regular form flow (desk, payment returns) keep using it even when premium is on. Invite links open premium.
const LEGACY_PARAMS = ['source', 'access', 'registrationId', 'outcome', 'step', 'enableCta', 'from', 'order_id', 'txnid'];

const Spinner = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-gray-700" />
  </div>
);

const Unavailable = ({ children }) => (
  <div className="flex min-h-screen items-center justify-center p-6 text-center text-gray-600">{children}</div>
);

/** /register/:event — premium landing when the event has it, otherwise the regular form. */
export const RegisterEntry = ({ legacy }) => {
  const { eventRef } = useEventRoute();
  const [params] = useSearchParams();
  const wantsLegacy = LEGACY_PARAMS.some((key) => params.has(key));
  const { data, isLoading, isError } = useTicketing(wantsLegacy ? null : eventRef);
  if (wantsLegacy) return legacy;
  if (isLoading) return <Spinner />;
  if (isError || !data?.ticketing) return legacy;
  return <TicketingLanding view={data} eventRef={eventRef} />;
};

/** /register/:event/on-spot — premium pass flow when the event says so, otherwise the regular desk form. */
export const OnSpotEntry = () => {
  const { eventRef } = useEventRoute();
  const { data, isLoading } = useTicketing(eventRef);
  return (
    <OnSpotAccessGate>
      {isLoading ? <Spinner /> : data?.ticketing?.onSpotFlow === 'PREMIUM'
        ? <TicketingLanding view={data} eventRef={eventRef} />
        : <EventRegistration />}
    </OnSpotAccessGate>
  );
};

/** /register/:event/passes/:group and /register/:event/book/:pass */
export const TicketingPage = ({ kind }) => {
  const { eventRef } = useEventRoute();
  const { groupId, passId } = useParams();
  const { data, isLoading, isError } = useTicketing(eventRef, kind === 'book' ? passId : undefined);
  if (isLoading) return <Spinner />;
  if (isError || !data?.ticketing) return <Unavailable>Registration for this event isn't available here.</Unavailable>;
  return kind === 'passes'
    ? <PassPicker view={data} eventRef={eventRef} groupId={groupId} />
    : <BookingForm view={data} eventRef={eventRef} passId={passId} />;
};
